/**
 * Normalizaciones de texto que tienen que dar el mismo resultado en el
 * servidor, en la web y en el kiosco. Si cada superficie normaliza a su
 * manera, una placa buscada no encuentra la que se guardó.
 */

/**
 * Un slug válido para tópicos MQTT y para identificadores estables de
 * condominio, punto de acceso y carril. Deliberadamente estrecho: no admite
 * `/`, `+` ni `#`, que son separador y comodines de MQTT. Un slug con
 * comodines permitiría a un aparato publicar fuera de su rama del árbol.
 */
export const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

export function esSlug(valor: string): boolean {
  return SLUG_RE.test(valor);
}

/**
 * "Acceso Principal" → "acceso-principal", "Peña Alta" → "pena-alta".
 *
 * NFD descompone la letra acentuada en letra + marca combinante, y `\p{Mn}`
 * borra la marca. Es la forma sin ambigüedad de quitar acentos: escribir el
 * rango de combinantes a mano deja caracteres invisibles en el código fuente.
 *
 * El resultado se **guarda**, no se recalcula en cada uso: si alguien renombra
 * "Acceso Principal" a "Entrada Norte", el slug sigue siendo el mismo y los
 * aparatos ya configurados no se quedan hablando a un tópico que dejó de
 * existir.
 */
export function aSlug(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Placa en la forma canónica del padrón: mayúsculas, sin espacios ni guiones.
 * Es la que sostiene la búsqueda del guardia y, más adelante, el cotejo del LPR.
 */
export function normalizarPlaca(placa: string): string {
  return placa.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * EPC tal como se compara contra el padrón: hex en mayúsculas, sin
 * separadores. Los lectores publican el mismo tag como `E280-1170...`,
 * `e2801170...` o con espacios, según el fabricante.
 */
export function normalizarEpc(epc: string): string {
  return epc.toUpperCase().replace(/[^0-9A-F]/g, '');
}

/**
 * Un teléfono, en la única forma con la que se puede comparar.
 *
 * ## Por qué hace falta
 *
 * `5551234567` y `+52 55 5123 4567` son el mismo teléfono y eran **dos
 * personas distintas**: el login compara el teléfono con igualdad exacta
 * —mientras el correo va sin distinguir mayúsculas— así que quien capturó su
 * cuenta con espacios no podía entrar escribiéndolo sin ellos. Y el índice
 * único no lo impedía, porque para Postgres son dos cadenas diferentes.
 *
 * Hoy el daño está contenido porque un servidor de caseta tiene un solo
 * condominio y los padrones se capturan a mano y parejo. En una nube con
 * varios, el teléfono pasa a ser **el identificador** de una persona, y dos
 * formas del mismo número son dos cuentas que nadie va a poder juntar.
 *
 * ## E.164 y no diez dígitos
 *
 * Porque es la forma sin ambigüedad, y porque es la que pide WhatsApp — que es
 * por donde va a entrar el residente que no quiere instalar una app. Guardar
 * diez dígitos da por hecho que México es el único país para siempre.
 *
 * Devuelve `null` cuando no reconoce el número, y **quien llama decide qué
 * hacer**: el esquema del padrón lo rechaza; el login lo trata como «entonces
 * no es un teléfono, será un correo».
 */
export function normalizarTelefono(entrada: string): string | null {
  // Se conserva si venía con `+`, porque es lo que distingue «marcó el código
  // de país» de «escribió diez dígitos y ya».
  const bruto = entrada.trim();
  const traiaMas = bruto.startsWith('+');
  const soloDigitos = bruto.replace(/\D/g, '');

  if (traiaMas) {
    // El prefijo de salida internacional no es parte del número. Alguien que
    // escribe `+00 52 …` está mezclando las dos formas de decir lo mismo, y sin
    // recortarlo saldría `+0052…` — que parece bien formado y **E.164 nunca
    // empieza en cero**, así que entraría a la columna de identidad como basura
    // creíble.
    const inter = soloDigitos.replace(/^00+/, '');
    if (inter.length < 8 || inter.length > 15) return null;

    // **`+521` y `+52` son el mismo número.** El `1` era el prefijo para marcar
    // a celulares y México lo quitó en 2019, pero WhatsApp lo sigue arrastrando
    // y así llegan capturados muchos padrones. Sin unirlos, el mismo residente
    // tendría dos cuentas — que es exactamente lo que esta función existe para
    // impedir.
    const mexicanoConElUnoViejo = /^521(\d{10})$/.exec(inter);
    if (mexicanoConElUnoViejo) return `+52${mexicanoConElUnoViejo[1]}`;

    return `+${inter}`;
  }

  // Sin `+`, se asume México. Los cuatro patrones que la gente escribe de
  // verdad, y el orden importa: se prueban de más específico a menos, porque
  // varios comparten longitud —`01` y `52` miden doce; `044`/`045` y `521`
  // miden trece— y lo que los separa es el prefijo, no el largo.
  const nacional =
    // `01` lleva dos dígitos y `044`/`045` llevan tres: no caben en la misma
    // forma, y meterlos juntos fue mi primer intento — lo atrapó la prueba.
    /^0(?:1|4[45])(\d{10})$/.exec(soloDigitos)?.[1] ?? // larga distancia y el celular de antes
    /^521(\d{10})$/.exec(soloDigitos)?.[1] ?? // 521 + 10, el celular con el 1 viejo
    /^52(\d{10})$/.exec(soloDigitos)?.[1] ?? // 52 + 10, el país sin el +
    (soloDigitos.length === 10 ? soloDigitos : null); // los diez de siempre

  return nacional ? `+52${nacional}` : null;
}

/**
 * El mismo teléfono, como lo espera ver quien lo capturó.
 *
 * `+523312345678` es correcto y nadie escribe así. Esto es **solo para
 * pantalla**: comparar, buscar o guardar siempre va en la forma canónica, o
 * volvemos justo al problema que `normalizarTelefono` resuelve.
 */
export function telefonoParaMostrar(guardado: string): string {
  const mexicano = /^\+52(\d{10})$/.exec(guardado);
  return mexicano ? mexicano[1]! : guardado;
}
